import { ContentUnit, type Item, type ItemCatalog, type ItemCreate, ItemPatch, ItemStatus, Sensitivity, ShelfLifeClass, Sku, UnitCode } from '@morales/shared';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState, type InputHTMLAttributes, type ReactNode } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api } from '../api';
import { useAuth } from '../auth';
import { Empty, ErrorBox, InactiveBadge, PageHead, QueryView, SearchBox } from '../components/ui';
import { formatDate, formatMoney, formatQty, formatStamp, packLabel } from '../format';
import { useDebounced, useDesktop, useSearchParam } from '../hooks';
import { ItemProfileSections } from '../components/ItemProfile';
import { Sheet, type SheetColumn } from '../components/Sheet';
import { byNumber, byText, useSort } from '../components/useSort';
import { useLocale } from '../i18n';
import type { AppMessageKey } from '../messages';
import { qk } from '../queryClient';

/** Owner and office keep the catalog; everyone else reads it. */
export function canEditCatalog(role: string | undefined): boolean {
  return role === 'owner' || role === 'office';
}

type ItemSort = 'name' | 'sku' | 'vendor' | 'cost' | 'costDate' | 'box' | 'origin';


export function ItemsPage() {
  const desktop = useDesktop();
  return desktop ? <ItemsSheet /> : <ItemsList />;
}

/** The catalog on the sheet: every product with its cost, each tier's price and the margin at the lowest one. */
function ItemsSheet() {
  const { t, intlLocale } = useLocale();
  const { me } = useAuth();
  const navigate = useNavigate();
  const [q, setQ] = useSearchParam('q');
  const [showInactive, setShowInactive] = useState(false);
  const term = useDebounced(q.trim());
  const books = canEditCatalog(me?.role);
  const list = useQuery({ queryKey: qk.items(term), queryFn: ({ signal }) => api.items(term, signal), placeholderData: keepPreviousData });
  const sorters = useMemo(() => {
    const base: Record<string, (a: Item, b: Item) => number> = {
      name: byText<Item>((i) => i.name),
      sku: byText<Item>((i) => i.sku),
      vendor: byText<Item>((i) => i.homeVendor),
      cost: byNumber<Item>((i) => i.cost?.amount),
      costDate: byText<Item>((i) => i.cost?.effectiveDate),
      box: byNumber<Item>((i) => i.boxWeightLb),
      origin: byText<Item>((i) => i.originNow),
    };
    return base as Record<ItemSort, (a: Item, b: Item) => number>;
  }, []);
  const { sorted, head } = useSort<Item, ItemSort>(sorters, { key: 'name', dir: 1 }, ['cost', 'costDate']);
  const rows = sorted((list.data ?? []).filter((i) => showInactive || i.active));
  const money = (v: number | null | undefined) => (v === null || v === undefined ? '' : formatMoney(v, intlLocale));
  const withCost = rows.filter((i) => i.cost).length;

  const columns: SheetColumn<Item>[] = [
    {
      key: 'name',
      header: head('name', t('name')),
      label: t('name'),
      width: '180px',
      render: (i) => (
        <span>
          <Link to={`/items/${i.id}`}>{i.name}</Link>
          {i.active ? null : <span className="cell-vendor"> · {t('inactive')}</span>}
        </span>
      ),
    },
    { key: 'sku', header: head('sku', 'SKU'), label: 'SKU', width: '120px', render: (i) => i.sku ?? '' },
    { key: 'vendor', header: head('vendor', t('vendor')), label: t('vendor'), width: '96px', render: (i) => i.homeVendor ?? '' },
    ...(books
      ? ([
          { key: 'cost', header: head('cost', t('cost')), label: t('cost'), width: '84px', align: 'right', render: (i) => (i.cost ? money(i.cost.amount) : ''), cellClass: (i) => (i.cost ? undefined : 'src-pattern') },
          { key: 'costDate', header: head('costDate', t('costDateCol')), label: t('costDateCol'), width: '100px', render: (i) => (i.cost ? formatDate(i.cost.effectiveDate, intlLocale, 'date') : '') },
        ] satisfies SheetColumn<Item>[])
      : []),
    { key: 'pack', header: t('pack'), label: t('pack'), width: '90px', render: (i) => packLabel(i) },
    { key: 'box', header: head('box', t('boxCol')), label: t('boxCol'), hint: t('boxHint'), width: '100px', render: (i) => boxText(i, t) },
    { key: 'origin', header: head('origin', t('originNow')), label: t('originNow'), hint: t('originNowHint'), width: '140px', render: (i) => i.originNow ?? '', cellClass: (i) => (i.originNow ? undefined : 'src-pattern') },
  ];

  return (
    <div className="desk-page">
      <header className="desk-head">
        <div>
          <h1>{t('navProducts')}</h1>
          <p className="muted">{t('itemsHint')}</p>
        </div>
        {books ? (
          <div className="row-actions">
            <Link to="/items/inbox" className="btn btn-secondary btn-sm">
              {t('openInbox')}
            </Link>
            <Link to="/items/codes" className="btn btn-secondary btn-sm">
              {t('vendorCodesTitle')}
            </Link>
            <Link to="/items/new" className="btn btn-primary btn-sm">
              + {t('newProduct')}
            </Link>
          </div>
        ) : null}
      </header>
      <div className="toolbar">
        <input className="tool-search" type="search" placeholder={t('findItem')} aria-label={t('findItem')} value={q} onChange={(e) => setQ(e.target.value)} />
        <label className="tool check">
          <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
          {t('showInactive')}
        </label>
      </div>
      <dl className="totals-strip" data-testid="item-totals">
        <div>
          <dt>{t('itemsCol')}</dt>
          <dd>{rows.length}</dd>
        </div>
        {books ? (
          <>
            <div className={withCost < rows.length ? 'warn' : undefined}>
              <dt>{t('noCostCol')}</dt>
              <dd>{rows.length - withCost}</dd>
            </div>
          </>
        ) : null}
      </dl>
      <QueryView query={list}>
        {() =>
          rows.length === 0 ? (
            <p className="muted pick-first">{t('noResults')}</p>
          ) : (
            <Sheet label={t('navProducts')} columns={columns} rows={rows} rowKey={(i) => i.id} rowLabel={(i) => i.name} stickyFirst minWidth={books ? '940px' : '760px'} onOpen={(i) => navigate(`/items/${i.id}`)} rowClass={(i) => (i.active ? undefined : 'zero')} />
          )
        }
      </QueryView>
    </div>
  );
}

/** The phone's list: big rows, the box and where it comes from now on the right (prices live on Price management). */
function ItemsList() {
  const { t, intlLocale } = useLocale();
  const { me } = useAuth();
  const [q, setQ] = useSearchParam('q');
  const term = useDebounced(q.trim());
  const list = useQuery({
    queryKey: qk.items(term),
    queryFn: ({ signal }) => api.items(term, signal),
    placeholderData: keepPreviousData,
  });

  return (
    <div className="stack">
      <h1>{t('navProducts')}</h1>
      {canEditCatalog(me?.role) ? (
        <>
          <Link to="/items/new" className="btn btn-primary">
            + {t('newProduct')}
          </Link>
          <Link to="/items/inbox" className="btn btn-secondary">
            {t('openInbox')}
          </Link>
        </>
      ) : null}
      <SearchBox value={q} onChange={setQ} />
      <QueryView query={list}>
        {(items) =>
          items.length === 0 ? (
            <Empty>{t('noResults')}</Empty>
          ) : (
            <ul className="list">
              {items.map((item) => {
                return (
                  <li key={item.id}>
                    <Link to={`/items/${item.id}`} className="row">
                      <span className="row-main">
                        <span className="row-title">{item.name}</span>
                        <span className="row-sub">
                          {item.sku ? <span className="label">{item.sku}</span> : null}
                          {packLabel(item)}
                          {item.active ? null : <InactiveBadge />}
                        </span>
                        {/* a role that may not see costs gets none from the server: no "No cost" line for it */}
                        {canEditCatalog(me?.role) ? (
                          <span className="row-sub">
                            {item.cost
                              ? `${t('cost')} ${formatMoney(item.cost.amount, intlLocale)} · ${formatDate(item.cost.effectiveDate, intlLocale, 'date')}${item.cost.vendorName ? ` · ${t('costFrom').replace('{vendor}', item.cost.vendorName)}` : ''}`
                              : t('noCost')}
                          </span>
                        ) : null}
                      </span>
                      {item.boxWeightLb || item.boxCount || item.originNow ? (
                        <span className="row-end">
                          <span className="row-end-label">{item.originNow ?? t('boxCol')}</span>
                          <span className="row-end-value">{boxText(item, t)}</span>
                        </span>
                      ) : null}
                    </Link>
                  </li>
                );
              })}
            </ul>
          )
        }
      </QueryView>
    </div>
  );
}

/** The box as bought: "25 lb · 48 ct" (decision 138). */
export function boxText(item: Pick<Item, 'boxWeightLb' | 'boxCount'>, t: (k: AppMessageKey) => string): string {
  return [item.boxWeightLb ? `${formatQty(item.boxWeightLb)} lb` : null, item.boxCount ? `${item.boxCount} ${t('countShort')}` : null].filter(Boolean).join(' · ');
}


/** How the item sells, as words: whole, half, loose (docs/CATALOG.md). */
export function sellsAs(item: Pick<Item, 'sellHalf' | 'sellLoose'>, t: (k: AppMessageKey) => string): string {
  const parts = [t('whole')];
  if (item.sellHalf) parts.push(t('half'));
  if (item.sellLoose) parts.push(t('loose'));
  return parts.join(' · ');
}

function Fact({ label, children }: { label: ReactNode; children: ReactNode }) {
  return (
    <div className="fact">
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

export function ItemDetailPage() {
  const { t, intlLocale } = useLocale();
  const { me } = useAuth();
  const { id = '' } = useParams();
  const qc = useQueryClient();
  const editable = canEditCatalog(me?.role);
  // ?edit=1 opens the form at once (the order day's "set a home vendor" links here)
  const [params, setParams] = useSearchParams();
  const [editing, setEditing] = useState(false);
  const askedEdit = params.get('edit') === '1';
  const query = useQuery({
    queryKey: qk.item(id),
    queryFn: ({ signal }) => api.item(id, signal),
    // Show the row we already have from the list right away, then refresh it.
    initialData: () =>
      qc
        .getQueriesData<Item[]>({ queryKey: ['items', 'list'] })
        .flatMap(([, rows]) => rows ?? [])
        .find((i) => i.id === id),
    initialDataUpdatedAt: 0,
  });
  const catalog = useQuery({ queryKey: qk.itemCatalog(id), queryFn: ({ signal }) => api.itemCatalog(id, signal) });
  // once who is signed in is known and the item is fresh from the server (the list's copy may be older, and a save
  // from it would send back what someone changed since); the address drops ?edit so Back does not reopen it
  const fresh = query.isFetchedAfterMount;
  useEffect(() => {
    if (askedEdit && editable && fresh) {
      setEditing(true);
      setParams({}, { replace: true });
    }
  }, [askedEdit, editable, fresh, setParams]);

  return (
    <QueryView query={query}>
      {(item) =>
        editing ? (
          <ItemForm
            item={item}
            onDone={() => setEditing(false)}
          />
        ) : (
          <div className="stack">
            <PageHead title={item.name} back="/items" />
            <div className="badges">
              {item.sku ? <span className="badge">{item.sku}</span> : null}
              {item.status !== 'active' ? <span className="badge badge-warn">{t(`status_${item.status}`)}</span> : null}
              {item.active ? null : <InactiveBadge />}
            </div>
            {editable ? (
              <button type="button" className="btn btn-secondary" onClick={() => setEditing(true)}>
                {t('edit')}
              </button>
            ) : null}

            <section className="card stack-sm">
              <h2>{t('identity')}</h2>
              <dl className="facts plain">
                <Fact label={t('nameEs')}>{item.nameEs ?? '—'}</Fact>
                <Fact label={t('family')}>{item.family ?? '—'}</Fact>
                <Fact label={t('variety')}>{item.variety ?? '—'}</Fact>
                <Fact label={t('category')}>{item.category ?? '—'}</Fact>
              </dl>
              {item.internalNote ? (
                <div className="stack-sm">
                  <h3 className="label">{t('internalNote')}</h3>
                  <p className="note">{item.internalNote}</p>
                </div>
              ) : null}
              <Aliases itemId={item.id} catalog={catalog.data} editable={editable} />
            </section>

            <section className="card stack-sm">
              <h2>{t('buying')}</h2>
              <dl className="facts plain">
                <Fact label={t('pack')}>{packLabel(item)}</Fact>
                <Fact label={t('content')}>{item.content && item.contentUnit ? `${item.content} ${item.contentUnit}` : '—'}</Fact>
                <Fact label={t('vendor')}>{item.homeVendor ?? '—'}</Fact>
                <Fact label={t('shelfLife')}>{t(`shelf_${item.shelfLifeClass}`)}</Fact>
                <Fact label={t('keepOnHand')}>{item.keepOnHandPacks ?? '—'}</Fact>
              </dl>
              <ProductCodes item={item} catalog={catalog.data} editable={editable} />
            </section>

            <ItemProfileSections item={item} editable={editable} />

            <section className="card stack-sm">
              <h2>{t('selling')}</h2>
              <Link to={`/items/${item.id}/intel`} className="btn btn-secondary btn-block">
                {t('intelLink')}
              </Link>
              {canEditCatalog(me?.role) ? (
                <Link to={`/items/${item.id}/history`} className="btn btn-secondary btn-block">
                  {t('phLink')}
                </Link>
              ) : null}
              <p className="muted">
                {t('pricesLiveOnPm')}{' '}
                {/* Price management is the owner's and the office's; the others read the price list */}
                {editable ? <Link to={`/prices?q=${encodeURIComponent(item.name)}`}>{t('openPriceManagement')}</Link> : <Link to="/prices/list">{t('pricesListLink')}</Link>}
              </p>
              <dl className="facts plain">
                <Fact label={t('sellUnits')}>{sellsAs(item, t)}</Fact>
                <Fact label={t('sensitivity')}>{t(`sens_${item.sensitivity}`)}</Fact>
                {editable ? (
                  <Fact label={t('cost')}>
                    {item.cost ? `${formatMoney(item.cost.amount, intlLocale)} · ${formatDate(item.cost.effectiveDate, intlLocale, 'date')}${item.cost.vendorName ? ` · ${t('costFrom').replace('{vendor}', item.cost.vendorName)}` : ''}` : t('noCost')}
                  </Fact>
                ) : null}
              </dl>
              {editable && catalog.data ? (
                <>
                  <h3 className="label">{t('rules')}</h3>
                  {catalog.data.rules.length === 0 ? (
                    <p className="muted">{t('noRules')}</p>
                  ) : (
                    <ul className="list">
                      {catalog.data.rules.map((r) => (
                        <li key={r.id} className="row">
                          <span className="row-main">
                            <span className="row-title">{r.customerName}</span>
                            <span className="row-sub">
                              {r.kind === 'fixed' && r.fixedPrice != null
                                ? `${t('fixed')} ${formatMoney(r.fixedPrice, intlLocale)}`
                                : `${r.marginPct ?? 0}% ${t('margin')}`}
                              {r.expiresAt ? ` · ${t('untilDate')} ${formatDate(r.expiresAt, intlLocale, 'date')}` : ''}
                            </span>
                            {r.reason ? <span className="row-sub">{r.reason}</span> : null}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              ) : null}
            </section>

            <section className="card stack-sm">
              <h2>{t('stockBlock')}</h2>
              {catalog.data ? (
                <dl className="facts plain">
                  <Fact label={t('onHand')}>
                    {formatQty(catalog.data.stock.onHandPacks)} {item.packUnit}
                  </Fact>
                  {catalog.data.stock.lastCount ? (
                    <>
                      <Fact label={t('countedAt')}>{formatStamp(catalog.data.stock.lastCount.countedAt, intlLocale, 'date')}</Fact>
                      <Fact label={t('sealedOpen')}>
                        {catalog.data.stock.lastCount.sealed ?? catalog.data.stock.lastCount.countedPacks}
                        {catalog.data.stock.lastCount.openRemaining != null
                          ? ` · ${catalog.data.stock.lastCount.openRemaining} ${catalog.data.stock.lastCount.openRemainingUnit === 'unit' ? item.packUnit : (catalog.data.stock.lastCount.openRemainingUnit ?? '')}`
                          : ''}
                      </Fact>
                      {catalog.data.stock.lastCount.systemPacks != null ? (
                        <Fact label={t('lastDrift')}>
                          {Math.round((catalog.data.stock.lastCount.countedPacks - catalog.data.stock.lastCount.systemPacks) * 100) / 100} {item.packUnit}
                        </Fact>
                      ) : null}
                    </>
                  ) : (
                    <Fact label={t('lastCount')}>{t('noCount')}</Fact>
                  )}
                  <Fact label={t('moved28')}>
                    {formatQty(catalog.data.stock.movedLast28Days)} {item.packUnit}
                  </Fact>
                </dl>
              ) : catalog.error ? (
                <ErrorBox error={catalog.error} />
              ) : (
                <p className="muted">{t('loading')}</p>
              )}
            </section>
          </div>
        )
      }
    </QueryView>
  );
}

function Aliases({ itemId, catalog, editable }: { itemId: string; catalog: ItemCatalog | undefined; editable: boolean }) {
  const { t } = useLocale();
  const qc = useQueryClient();
  const [draft, setDraft] = useState('');
  const refresh = () => Promise.all([qc.invalidateQueries({ queryKey: qk.itemCatalog(itemId) }), qc.invalidateQueries({ queryKey: ['items', 'list'] })]);
  const add = useMutation({
    mutationFn: (alias: string) => api.addAlias(itemId, { alias, source: 'manual' }),
    onSuccess: () => {
      setDraft('');
      void refresh();
    },
  });
  const remove = useMutation({ mutationFn: (aliasId: string) => api.removeAlias(itemId, aliasId), onSuccess: () => void refresh() });
  if (!catalog) return null;
  return (
    <div className="stack-sm">
      <h3 className="label">{t('aliases')}</h3>
      {catalog.aliases.length === 0 ? (
        <p className="muted">{t('noAliases')}</p>
      ) : (
        <ul className="chips">
          {catalog.aliases.map((a) => (
            <li key={a.id} className="chip">
              {a.alias}
              {editable ? (
                <button type="button" className="chip-x" aria-label={`${t('remove')}: ${a.alias}`} onClick={() => { if (window.confirm(`${t('remove')}: ${a.alias}. ${t('confirmSure')}`)) remove.mutate(a.id); }} disabled={remove.isPending}>
                  ×
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      )}
      {editable ? (
        <form
          className="picker-head"
          onSubmit={(e) => {
            e.preventDefault();
            const alias = draft.trim();
            if (alias) add.mutate(alias);
          }}
        >
          <input className="search" type="text" aria-label={t('addAlias')} placeholder={t('addAlias')} value={draft} onChange={(e) => setDraft(e.target.value)} autoComplete="off" />
          <button type="submit" className="btn btn-secondary" disabled={add.isPending || draft.trim() === ''}>
            {t('addAlias')}
          </button>
        </form>
      ) : null}
      {add.error ? <ErrorBox error={add.error} /> : null}
      {remove.error ? <ErrorBox error={remove.error} /> : null}
    </div>
  );
}

/** A product's vendor codes (decision 150): each vendor's code with its cost; the owner and office unlink one or add one. */
function ProductCodes({ item, catalog, editable }: { item: Item; catalog: ItemCatalog | undefined; editable: boolean }) {
  const { t, intlLocale } = useLocale();
  const qc = useQueryClient();
  const vendors = useQuery({ queryKey: qk.vendors, queryFn: ({ signal }) => api.vendors(signal), staleTime: 10 * 60_000, enabled: editable });
  const [vendorId, setVendorId] = useState('');
  const [code, setCode] = useState('');
  const [desc, setDesc] = useState('');
  const vendor = vendorId || item.homeVendorId || vendors.data?.find((v) => /mata/i.test(v.name))?.id || vendors.data?.[0]?.id || '';
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: qk.itemCatalog(item.id) });
    void qc.invalidateQueries({ queryKey: ['vendor-codes'] });
    void qc.invalidateQueries({ queryKey: ['items'] });
  };
  const add = useMutation({
    mutationFn: () => api.addVendorCode(vendor, { vendorCode: code.trim(), ...(desc.trim() ? { vendorDesc: desc.trim() } : {}), itemId: item.id }),
    onSuccess: () => {
      setCode('');
      setDesc('');
      refresh();
    },
  });
  const unlink = useMutation({ mutationFn: (v: { vendorId: string; id: string }) => api.changeVendorCode(v.vendorId, v.id, { from: item.id, itemId: null }), onSuccess: refresh });
  if (!catalog) return null;
  return (
    <div className="stack-sm">
      <h3 className="label">{t('vendorCodes')}</h3>
      {catalog.vendorCodes.length === 0 ? (
        <p className="muted">{t('noVendorCodes')}</p>
      ) : (
        <ul className="list">
          {catalog.vendorCodes.map((vc) => (
            <li key={`${vc.vendorId}:${vc.vendorCode}`} className="row">
              <span className="row-main">
                <span className="row-title">{vc.vendor}</span>
                <span className="row-sub">
                  {vc.vendorCode}
                  {vc.vendorDesc ? ` · ${vc.vendorDesc}` : ''}
                  {vc.isPrimary ? <span className="badge badge-ok">{t('preferred')}</span> : null}
                </span>
              </span>
              {vc.cost != null && vc.costDate ? (
                <span className="row-end">
                  <span className="row-end-label">{formatDate(vc.costDate, intlLocale, 'date')}</span>
                  <span className="row-end-value">{formatMoney(vc.cost, intlLocale)}</span>
                </span>
              ) : null}
              {editable && vc.id ? (
                <button type="button" className="btn btn-ghost btn-sm" disabled={unlink.isPending} aria-label={`${t('codeUnlink')}: ${vc.vendorCode}`} onClick={() => {
                    if (window.confirm(t('codeUnlinkAsk').replace('{code}', vc.vendorCode).replace('{product}', item.name))) unlink.mutate({ vendorId: vc.vendorId, id: vc.id! });
                  }}>
                  {t('codeUnlink')}
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      )}
      {editable ? (
        <form
          className="stack-sm"
          onSubmit={(e) => {
            e.preventDefault();
            if (code.trim() && vendor) add.mutate();
          }}
        >
          <div className="picker-head">
            <select value={vendor} onChange={(e) => setVendorId(e.target.value)} aria-label={t('vendor')}>
              {(vendors.data ?? []).map((v) => (
                <option key={v.id} value={v.id}>
                  {v.name}
                </option>
              ))}
            </select>
            <input className="search" type="text" aria-label={t('vendorCodeCol')} placeholder={t('vendorCodeCol')} value={code} onChange={(e) => setCode(e.target.value)} autoComplete="off" maxLength={60} />
            <input className="search" type="text" aria-label={t('vendorDescCol')} placeholder={t('vendorDescCol')} value={desc} onChange={(e) => setDesc(e.target.value)} autoComplete="off" maxLength={200} />
            <button type="submit" className="btn btn-secondary" disabled={add.isPending || !code.trim() || !vendor}>
              {t('codeAdd')}
            </button>
          </div>
          <p className="muted small">{t('codeAddHint')}</p>
        </form>
      ) : null}
      {add.error ? <ErrorBox error={add.error} /> : null}
      {unlink.error ? <ErrorBox error={unlink.error} /> : null}
    </div>
  );
}

interface Draft {
  name: string;
  nameEs: string;
  sku: string;
  family: string;
  variety: string;
  content: string;
  contentUnit: '' | NonNullable<Item['contentUnit']>;
  sellHalf: boolean;
  sellLoose: boolean;
  sensitivity: Item['sensitivity'];
  status: Item['status'];
  shelfLifeClass: Item['shelfLifeClass'];
  keepOnHandPacks: string;
  internalNote: string;
  homeVendorId: string;
  packDesc: string;
  categoryId: string;
}

function toDraft(item: Item): Draft {
  return {
    name: item.name,
    nameEs: item.nameEs ?? '',
    sku: item.sku ?? '',
    family: item.family ?? '',
    variety: item.variety ?? '',
    content: item.content != null ? String(item.content) : '',
    contentUnit: item.contentUnit ?? '',
    sellHalf: item.sellHalf,
    sellLoose: item.sellLoose,
    sensitivity: item.sensitivity,
    status: item.status,
    shelfLifeClass: item.shelfLifeClass,
    keepOnHandPacks: item.keepOnHandPacks != null ? String(item.keepOnHandPacks) : '',
    internalNote: item.internalNote ?? '',
    homeVendorId: item.homeVendorId ?? '',
    packDesc: item.packDesc ?? '',
    categoryId: item.categoryId ?? '',
  };
}

const text = (s: string) => (s.trim() === '' ? null : s.trim());
const num = (s: string) => (s.trim() === '' ? null : Number(s.trim().replace(',', '.')));

/** The PATCH body for what changed, or a message key when something typed is not usable. */
export function toPatch(item: Item, d: Draft): { patch: ItemPatch } | { problem: AppMessageKey } {
  const sku = text(d.sku)?.toUpperCase() ?? null;
  // only a SKU typed now is checked: one made by the database or set before is kept as it is
  if (sku !== null && sku !== item.sku && !Sku.safeParse(sku).success) return { problem: 'skuFormat' };
  const content = num(d.content);
  const keep = num(d.keepOnHandPacks);
  if ((content !== null && !(content > 0)) || (keep !== null && !(keep >= 0))) return { problem: 'error' };
  const contentUnit = content !== null && d.contentUnit ? d.contentUnit : null;
  const full: ItemPatch = {
    name: d.name.trim() || item.name,
    nameEs: text(d.nameEs),
    sku,
    family: text(d.family),
    variety: text(d.variety),
    content: contentUnit ? content : null,
    contentUnit,
    sellHalf: d.sellHalf,
    sellLoose: d.sellLoose,
    sensitivity: d.sensitivity,
    status: d.status,
    shelfLifeClass: d.shelfLifeClass,
    keepOnHandPacks: keep,
    internalNote: text(d.internalNote),
    homeVendorId: d.homeVendorId || null,
    packDesc: text(d.packDesc),
    categoryId: d.categoryId || null,
  };
  const before: Record<string, unknown> = { ...item };
  const patch: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(full)) if (before[k] !== v) patch[k] = v;
  const parsed = ItemPatch.safeParse(patch);
  return parsed.success ? { patch: parsed.data } : { problem: 'error' };
}

function ItemForm({ item, onDone }: { item: Item; onDone: () => void }) {
  const { t } = useLocale();
  const qc = useQueryClient();
  const [d, setD] = useState<Draft>(() => toDraft(item));
  const [problem, setProblem] = useState<AppMessageKey | null>(null);
  // Fruit, Vegetable, Dry goods: where the product sits on the printed price list (decision 167)
  const categories = useQuery({ queryKey: ['items', 'categories'], queryFn: ({ signal }) => api.itemCategories(signal), staleTime: 10 * 60_000 });
  const save = useMutation({
    mutationFn: (patch: ItemPatch) => api.patchItem(item.id, patch),
    onSuccess: (updated) => {
      qc.setQueryData(qk.item(item.id), updated);
      void qc.invalidateQueries({ queryKey: ['items'] });
      onDone();
    },
  });
  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => {
    setProblem(null);
    setD((cur) => ({ ...cur, [k]: v }));
  };
  function submit() {
    const r = toPatch(item, d);
    if ('problem' in r) return setProblem(r.problem);
    if (Object.keys(r.patch).length === 0) return onDone();
    save.mutate(r.patch);
  }
  const field = (k: 'name' | 'nameEs' | 'sku' | 'family' | 'variety', label: AppMessageKey, extra: InputHTMLAttributes<HTMLInputElement> = {}) => (
    <label className="field">
      <span>{t(label)}</span>
      <input type="text" autoComplete="off" value={d[k]} onChange={(e) => set(k, e.target.value)} {...extra} />
    </label>
  );
  return (
    <form
      className="stack"
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      <PageHead title={`${t('edit')}: ${item.name}`} />
      <section className="card stack-sm">
        <h2>{t('identity')}</h2>
        {field('name', 'name')}
        <p className="muted">{t('nameHint')}</p>
        {field('nameEs', 'nameEs')}
        {field('sku', 'sku', { placeholder: 'ONI-YEL-50LB', style: { textTransform: 'uppercase' } })}
        {field('family', 'family')}
        {field('variety', 'variety')}
        <label className="field">
          <span>{t('productCategory')}</span>
          <select value={d.categoryId} onChange={(e) => set('categoryId', e.target.value)}>
            <option value="">{t('productCategoryNone')}</option>
            {(categories.data ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>{t('internalNote')}</span>
          <textarea value={d.internalNote} onChange={(e) => set('internalNote', e.target.value)} maxLength={1000} />
          <span className="muted">{t('internalNoteHint')}</span>
        </label>
        <label className="field">
          <span>{t('status')}</span>
          <select value={d.status} onChange={(e) => set('status', ItemStatus.parse(e.target.value))}>
            {ItemStatus.options.map((s) => (
              <option key={s} value={s}>
                {t(`status_${s}`)}
              </option>
            ))}
          </select>
        </label>
      </section>
      <section className="card stack-sm">
        <h2>{t('buying')}</h2>
        <VendorPick value={d.homeVendorId} current={item.homeVendorId ? item.homeVendor : null} onChange={(v) => set('homeVendorId', v)} />
        <label className="field">
          <span>{t('packDescLabel')}</span>
          <input type="text" autoComplete="off" value={d.packDesc} onChange={(e) => set('packDesc', e.target.value)} placeholder="25 lb" maxLength={60} />
        </label>
        <div className="picker-head">
          <label className="field" style={{ flex: 1 }}>
            <span>{t('content')}</span>
            <input type="text" inputMode="decimal" autoComplete="off" value={d.content} onChange={(e) => set('content', e.target.value)} placeholder="50" />
          </label>
          <label className="field">
            <span>{t('stockUnit')}</span>
            <select value={d.contentUnit} onChange={(e) => set('contentUnit', e.target.value === '' ? '' : ContentUnit.parse(e.target.value))}>
              <option value="">—</option>
              {ContentUnit.options.map((u) => (
                <option key={u} value={u}>
                  {u}
                </option>
              ))}
            </select>
          </label>
        </div>
        <label className="field">
          <span>{t('shelfLife')}</span>
          <select value={d.shelfLifeClass} onChange={(e) => set('shelfLifeClass', ShelfLifeClass.parse(e.target.value))}>
            {ShelfLifeClass.options.map((s) => (
              <option key={s} value={s}>
                {t(`shelf_${s}`)}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>{t('keepOnHand')}</span>
          <input type="text" inputMode="decimal" autoComplete="off" value={d.keepOnHandPacks} onChange={(e) => set('keepOnHandPacks', e.target.value)} />
        </label>
      </section>
      <section className="card stack-sm">
        <h2>{t('selling')}</h2>
        <span className="label">{t('sellUnits')}</span>
        <div className="chips">
          <span className="chip" aria-pressed="true">
            {t('whole')}
          </span>
          <button type="button" className="chip" aria-pressed={d.sellHalf} onClick={() => set('sellHalf', !d.sellHalf)}>
            {t('half')}
          </button>
          <button type="button" className="chip" aria-pressed={d.sellLoose} onClick={() => set('sellLoose', !d.sellLoose)}>
            {t('loose')}
          </button>
        </div>
        <label className="field">
          <span>{t('sensitivity')}</span>
          <select value={d.sensitivity} onChange={(e) => set('sensitivity', Sensitivity.parse(e.target.value))}>
            {Sensitivity.options.map((s) => (
              <option key={s} value={s}>
                {t(`sens_${s}`)}
              </option>
            ))}
          </select>
        </label>
      </section>
      {problem ? (
        <p className="field-error" role="alert">
          {t(problem)}
        </p>
      ) : null}
      {save.error ? <ErrorBox error={save.error} /> : null}
      <div className="actionbar">
        <button type="button" className="btn btn-secondary" onClick={onDone} disabled={save.isPending}>
          {t('cancel')}
        </button>
        <button type="submit" className="btn btn-primary btn-lg" disabled={save.isPending}>
          {save.isPending ? t('sending') : t('save')}
        </button>
      </div>
    </form>
  );
}

/** The vendor a product is bought from: its tab on the order day, and whose cost prices it. */
function VendorPick({ value, current = null, onChange }: { value: string; current?: string | null; onChange: (id: string) => void }) {
  const { t } = useLocale();
  const vendors = useQuery({ queryKey: qk.vendors, queryFn: ({ signal }) => api.vendors(signal), staleTime: 10 * 60_000 });
  // the item's vendor may be archived (not in the list): still shown as what it is
  const missing = value !== '' && vendors.data !== undefined && !vendors.data.some((v) => v.id === value);
  return (
    <div className="stack-sm">
      <label className="field">
        <span>{t('homeVendor')}</span>
        <select value={value} onChange={(e) => onChange(e.target.value)}>
          <option value="">{t('noHomeVendor')}</option>
          {missing ? <option value={value}>{current ?? value}</option> : null}
          {(vendors.data ?? []).map((v) => (
            <option key={v.id} value={v.id}>
              {v.name}
            </option>
          ))}
        </select>
      </label>
      <p className="muted">{t('homeVendorHint')}</p>
    </div>
  );
}

/** Products → New product (decision 91): the name and how it is bought; the SKU and the prices follow by themselves. */
export function NewItemPage() {
  const { t } = useLocale();
  const { me } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  // from Vendor codes (decision 150): a vendor's code not in the catalog yet becomes the product, linked when saved
  const [params] = useSearchParams();
  const fromVendor = params.get('vendor');
  const fromCode = params.get('code');
  const [d, setD] = useState({ name: titleCase(params.get('name') ?? ''), nameEs: '', packUnit: 'CS' as Item['packUnit'], packDesc: '', homeVendorId: fromVendor ?? '', keepOnHandPacks: '' });
  const [problem, setProblem] = useState<AppMessageKey | null>(null);
  const create = useMutation({
    // the product and the link to the vendor's code are made in one step on the server, or neither is
    mutationFn: (body: ItemCreate) => api.createItem(fromVendor && fromCode ? { ...body, fromVendorCode: { vendorId: fromVendor, codeId: fromCode } } : body),
    onSuccess: (item) => {
      void qc.invalidateQueries({ queryKey: ['items'] });
      void qc.invalidateQueries({ queryKey: ['vendor-codes'] });
      void qc.invalidateQueries({ queryKey: qk.unknownNames });
      navigate(fromVendor && fromCode ? `/items/codes?vendor=${encodeURIComponent(fromVendor)}` : `/items/${item.id}`, { replace: true });
    },
  });
  if (!me) return null;
  if (!canEditCatalog(me.role)) return <Empty>{t('error')}</Empty>;
  const set = <K extends keyof typeof d>(k: K, v: (typeof d)[K]) => {
    setProblem(null);
    setD((cur) => ({ ...cur, [k]: v }));
  };
  function submit() {
    if (!d.name.trim()) return setProblem('newProductNameNeeded');
    const keep = num(d.keepOnHandPacks);
    if (keep !== null && !(keep >= 0)) return setProblem('error');
    create.mutate({ name: d.name.trim(), nameEs: text(d.nameEs), packUnit: d.packUnit, packDesc: text(d.packDesc), homeVendorId: d.homeVendorId || null, keepOnHandPacks: keep });
  }
  return (
    <form
      className="stack"
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      <PageHead title={t('newProduct')} back={fromCode ? '/items/codes' : '/items'} />
      {fromCode ? <p className="muted">{t('codeNewFrom').replace('{code}', params.get('label') ?? '')}</p> : null}
      <section className="card stack-sm">
        <label className="field">
          <span>{t('name')}</span>
          <input type="text" autoComplete="off" autoFocus value={d.name} onChange={(e) => set('name', e.target.value)} placeholder="Tomatillos 25lb" maxLength={120} />
        </label>
        <p className="muted">{t('nameHint')}</p>
        <label className="field">
          <span>{t('nameEs')}</span>
          <input type="text" autoComplete="off" value={d.nameEs} onChange={(e) => set('nameEs', e.target.value)} maxLength={120} />
        </label>
        <div className="picker-head">
          <label className="field">
            <span>{t('packUnitLabel')}</span>
            <select value={d.packUnit} onChange={(e) => set('packUnit', UnitCode.parse(e.target.value))}>
              {UnitCode.options.map((u) => (
                <option key={u} value={u}>
                  {u}
                </option>
              ))}
            </select>
          </label>
          <label className="field" style={{ flex: 1 }}>
            <span>{t('packDescLabel')}</span>
            <input type="text" autoComplete="off" value={d.packDesc} onChange={(e) => set('packDesc', e.target.value)} placeholder="25 lb" maxLength={60} />
          </label>
        </div>
        <VendorPick value={d.homeVendorId} onChange={(v) => set('homeVendorId', v)} />
        <label className="field">
          <span>{t('keepOnHand')}</span>
          <input type="text" inputMode="decimal" autoComplete="off" value={d.keepOnHandPacks} onChange={(e) => set('keepOnHandPacks', e.target.value)} />
        </label>
        <p className="muted">{t('newProductHint')}</p>
      </section>
      {problem ? (
        <p className="field-error" role="alert">
          {t(problem)}
        </p>
      ) : null}
      {create.error ? <ErrorBox error={create.error} /> : null}
      <div className="actionbar">
        <Link to="/items" className="btn btn-secondary">
          {t('cancel')}
        </Link>
        <button type="submit" className="btn btn-primary btn-lg" disabled={create.isPending}>
          {create.isPending ? t('sending') : t('addProduct')}
        </button>
      </div>
    </form>
  );
}

/** "TOMATO ROMA 25#" → "Tomato Roma 25#": a vendor's capitals as a product name to start from */
function titleCase(s: string): string {
  return s.trim().toLowerCase().replace(/(^|[\s(/-])([a-zñáéíóú])/g, (_m, sep: string, c: string) => sep + c.toUpperCase());
}
